/**
 * Promotions Component - Feature cards with special offers
 */

import React from 'react';
import { Promotion, UIText } from './types';
import { getImageUrl, formatPrice, getEventCurrency, getVatDisplayEnabled, getVatRate, calculateGrossPrice } from './utils';

interface PromotionsProps {
  promotions: Promotion[];
  appliedPromotions: Promotion[];
  onAddPromotion: (promotion: Promotion) => void;
  onRemovePromotion: (promotionId: string) => void;
  uiText: UIText['promotions'];
  promoValidity?: string; // Validity date from event settings
  promotionErrors?: Record<string, string>; // Error translation keys per promotion ID
}

export const Promotions: React.FC<PromotionsProps> = ({
  promotions,
  appliedPromotions,
  onAddPromotion,
  onRemovePromotion,
  uiText,
  promoValidity,
  promotionErrors = {}
}) => {
  const isApplied = (promotionId: string) => {
    return appliedPromotions.some(p => p.id === promotionId);
  };

  const handleTogglePromotion = (promotion: Promotion) => {
    if (isApplied(promotion.id)) {
      onRemovePromotion(promotion.id);
    } else {
      onAddPromotion(promotion);
    }
  };

  const getPromoImageUrl = (promotion: Promotion) => {
    // Use actual image path if available, otherwise use placeholder
    if (promotion.imagePath) {
      return getImageUrl(promotion.imagePath);
    }
    // Fallback to placeholder
    const encodedTitle = encodeURIComponent(promotion.title);
    return `https://placehold.co/80x80/f8f9fa/666666?text=${encodedTitle}`;
  };

  return (
    <section className="sprintry-promotions">
      <div className="sprintry-promotions__header">
        <h3 className="sprintry-promotions__title">{uiText.title}</h3>
        {promoValidity ? (
          <p className="sprintry-promotions__validity">{promoValidity}</p>
        ) : (
          <p className="sprintry-promotions__validity">{uiText.validUntil}</p>
        )}
      </div>

      <div className="sprintry-promotions__list">
        {promotions.map(promotion => {
          const applied = isApplied(promotion.id);
          const savings = promotion.originalPrice - promotion.promoPrice;
          const hasError = promotionErrors[promotion.id];

          return (
            <div
              key={promotion.id}
              className={`sprintry-promotion-card ${
                applied ? 'sprintry-promotion-card--applied' : ''
              } ${
                hasError ? 'sprintry-promotion-card--error' : ''
              }`}
            >
              <div
                className="sprintry-promotion-card__thumbnail"
                style={{ backgroundImage: `url(${getPromoImageUrl(promotion)})` }}
              />

              <div className="sprintry-promotion-card__info">
                <h4 className="sprintry-promotion-card__title">{promotion.title}</h4>
                <p className="sprintry-promotion-card__description">{promotion.description}</p>
              </div>

              <div className="sprintry-promotion-card__pricing">
                <span className="sprintry-promotion-card__original">
                  {formatPrice(promotion.originalPrice, promotion.currency || getEventCurrency())}
                </span>
                <span className="sprintry-promotion-card__promo">
                  {getVatDisplayEnabled() ? (
                    <span>
                      {formatPrice(calculateGrossPrice(promotion.promoPrice, getVatRate()), promotion.currency || getEventCurrency())}
                      <span className="sprintry-promotion-card__promo-label">
                        {uiText?.vatIncluded || 'incl. VAT'}
                      </span>
                    </span>
                  ) : (
                    formatPrice(promotion.promoPrice, promotion.currency || getEventCurrency())
                  )}
                  {getVatDisplayEnabled() && (
                    <span className="sprintry-promotion-card__promo-vat">
                      {formatPrice(promotion.promoPrice, promotion.currency || getEventCurrency())} {uiText?.vatExcluded || 'excl. VAT'}
                    </span>
                  )}
                </span>
                <span className="sprintry-promotion-card__savings">
                  -{formatPrice(savings, promotion.currency || getEventCurrency())}
                </span>
              </div>

              {hasError && (
                <div className="sprintry-promotion-card__error-message">
                  {(() => {
                    // Extract the error key from the full translation path
                    // e.g., 'promotions.errors.onlyOneTradeUp' -> 'onlyOneTradeUp'
                    const errorKey = hasError.split('.').pop() as keyof typeof uiText.errors;
                    return uiText.errors?.[errorKey] || hasError;
                  })()}
                </div>
              )}

              <button
                className={`sprintry-promotion-card__button ${applied ? 'sprintry-promotion-card__button--applied' : ''}`}
                onClick={() => handleTogglePromotion(promotion)}
              >
                {applied ? '✓' : '+'}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};